// PlacePreviewSheet - Overlay sheet showing place details without leaving the chat

import React from 'react';
import { X, MapPin, Clock, Star, Globe, Phone, Navigation, Heart } from 'lucide-react';
import type { Place } from '../../types';
import { WIFI_QUALITY_LABELS, POWER_OUTLET_LABELS } from '../../types';

interface PlacePreviewSheetProps {
  place: Place;
  onClose: () => void;
  onViewFullDetails: () => void;
  onToggleFavorite?: () => void;
  isFavorite?: boolean;
}

export function PlacePreviewSheet({
  place,
  onClose,
  onViewFullDetails,
  onToggleFavorite,
  isFavorite = false,
}: PlacePreviewSheetProps) {
  const imageUrl = place.images?.[0] || place.imageUrl;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="relative w-full max-w-lg bg-white rounded-t-3xl shadow-2xl max-h-[85vh] overflow-hidden animate-slide-up">
        {/* Header with Image */}
        <div className="relative">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={place.name}
              className="w-full h-48 object-cover"
            />
          ) : (
            <div className="w-full h-48 bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
              <MapPin className="w-16 h-16 text-indigo-300" />
            </div>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>

          {/* Favorite Button */}
          {onToggleFavorite && (
            <button
              onClick={onToggleFavorite}
              className="absolute top-3 left-3 w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
            >
              <Heart
                className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-700'}`}
              />
            </button>
          )}

          {/* Rating Badge */}
          {place.rating > 0 && (
            <div className="absolute bottom-3 left-3 flex items-center gap-1 px-2 py-1 bg-white/90 backdrop-blur rounded-full">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="text-sm font-medium">{place.rating.toFixed(1)}</span>
              <span className="text-xs text-gray-500">({place.reviewCount})</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[calc(85vh-12rem-4rem)]">
          {/* Title & Location */}
          <h2 className="text-xl font-bold text-gray-900">{place.name}</h2>
          <p className="text-sm text-gray-500 mt-1">
            {place.district && `${place.district}, `}{place.city}
          </p>

          {/* Tagline */}
          {place.tagline && (
            <p className="text-gray-700 mt-3 italic">"{place.tagline}"</p>
          )}

          {/* GoodFactor */}
          {(place.goodFactor || place.goodworkFactor) && (
            <div className="mt-4 p-3 bg-indigo-50 rounded-xl">
              <p className="text-sm font-medium text-indigo-900">
                {place.goodFactor || place.goodworkFactor}
              </p>
            </div>
          )}

          {/* Key Info Grid */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            {/* Opening Hours */}
            {place.openingHours?.displayText && (
              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-xl">
                <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Öffnungszeiten</p>
                  <p className="text-sm text-gray-900">{place.openingHours.displayText}</p>
                </div>
              </div>
            )}

            {/* Location */}
            {place.address && (
              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-xl">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">Adresse</p>
                  <p className="text-sm text-gray-900">{place.address}</p>
                </div>
              </div>
            )}
          </div>

          {/* Amenities (GoodWork) */}
          {(place.amenities || place.amenitiesText) && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Ausstattung</h3>
              {place.amenitiesText ? (
                <p className="text-sm text-gray-600">{place.amenitiesText}</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {place.amenities?.wifi && place.amenities.wifi !== 'none' && (
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                      {WIFI_QUALITY_LABELS[place.amenities.wifi]}
                    </span>
                  )}
                  {place.amenities?.powerOutlets && (
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                      {POWER_OUTLET_LABELS[place.amenities.powerOutlets]}
                    </span>
                  )}
                  {place.amenities?.meetingRooms && (
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                      Meeting-Räume
                    </span>
                  )}
                  {place.amenities?.kitchen && (
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                      Küche/Kaffee
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Vibe Tags */}
          {(place.vibeTags?.length || place.goodworkVibeTags?.length) ? (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Vibe</h3>
              <div className="flex flex-wrap gap-2">
                {(place.vibeTags || place.goodworkVibeTags || []).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {/* Contact Links */}
          {(place.website || place.phone) && (
            <div className="mt-4 flex gap-2">
              {place.website && (
                <a
                  href={place.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Globe className="w-4 h-4" />
                  Website
                </a>
              )}
              {place.phone && (
                <a
                  href={`tel:${place.phone}`}
                  className="flex-1 flex items-center justify-center gap-2 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Phone className="w-4 h-4" />
                  Anrufen
                </a>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-100 bg-white">
          <div className="flex gap-3">
            {place.latitude && place.longitude && (
              <a
                href={`https://maps.google.com/?q=${place.latitude},${place.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
              >
                <Navigation className="w-4 h-4" />
                Route
              </a>
            )}
            <button
              onClick={onViewFullDetails}
              className="flex-1 py-3 bg-indigo-500 text-white rounded-xl font-medium hover:bg-indigo-600 transition-colors"
            >
              Alle Details ansehen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlacePreviewSheet;
